import React, { useEffect, useCallback, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useOktaAuth } from '@okta/okta-react';
import { Link } from 'react-router-dom';

import { 
  FacebookShareButton, 
  LinkedinShareButton, 
  TelegramShareButton, 
  TwitterShareButton,
  FacebookIcon,
  LinkedinIcon,
  TelegramIcon,
  TwitterIcon
} from 'react-share';


import { fetchBeers, fetchMoreBeers } from "../actions/beerActions";
import { initAdSense } from "../services/googleServices";
import Search from "./search";
import Beers from "./beers";

const Home = ({ beers, page, isLoading, error, searchQuery, fetchBeers, fetchMoreBeers }) => {
  const { authState } = useOktaAuth();
  const [attempts, setAttempts] = useState(0);
  const [isGivingUp, setIsGivingUp] = useState(false);

  const isAuthenticated = Boolean(authState?.isAuthenticated);
  const hasOpenedCdResources = typeof window !== 'undefined' && 
    (sessionStorage.getItem('cd_resource_opened') === 'true' || 
     /(^|\.)clouddashboard\./i.test(window.location.hostname));
  const hasSearched = Boolean(searchQuery && searchQuery.trim().length > 0);

  // Unauthorised visitors or authorised visitors who opened CD resources (before search is done) see ads;
  // Once search is done, hide ads for authorised users and show Pro NFT card instead.
  // Pure PCD users never see ads.
  const shouldShowAd = !isAuthenticated || (isAuthenticated && hasOpenedCdResources && !hasSearched);
  const shouldShowNft = isAuthenticated && (!hasOpenedCdResources || hasSearched);

  useEffect(() => {
    if (shouldShowAd) {
      initAdSense();
    }
  }, [shouldShowAd]);

  useEffect(() => {
    // load beers if none are found in state
    if (beers && beers.length === 0) {
      fetchBeers(page);
    }
  }, [beers, fetchBeers, page, isGivingUp]);

  useEffect(() => {
    // Reset retry logic if data is successfully loaded
    if (beers && beers.length > 0 && (attempts > 0 || isGivingUp)) {
      setAttempts(0);
      setIsGivingUp(false);
    }
  }, [beers, attempts, isGivingUp]);

  useEffect(() => {
    // If there is an error and we haven't exhausted retries, start a 30s timer
    if (error && attempts < 5 && !isLoading) {
      const timer = setTimeout(() => {
        console.info(`Retry attempt ${attempts + 1} for health data sync...`);
        setAttempts(prev => prev + 1);
        fetchBeers(page);
      }, 30000);

      return () => clearTimeout(timer);
    }

    // If we reached 5 attempts and still have an error, give up
    if (error && attempts >= 5) {
      setIsGivingUp(true);
    }
  }, [error, attempts, isLoading, fetchBeers, page]);

  const onScroll = useCallback(() => {
    // exit function if beers are currently being loaded
    if (isLoading) return;

    // check if scroll is at the bottom of the page
    if (
      window.innerHeight + document.documentElement.scrollTop >
      document.documentElement.offsetHeight - 100
    ) {
      // load more beers and append to state
      fetchMoreBeers(page);
    }
  }, [isLoading, fetchMoreBeers, page]);

  useEffect(() => {
    window.addEventListener("scroll", onScroll, false);
    return () => window.removeEventListener("scroll", onScroll, false);
  }, [onScroll]);

  return (
    <>
      <header className="justify-content-center py-2 bg-warning text-center text-white">
        <div className="container">
          <div className="row">
            <div className="col">
              <h1 className="font-weight-bold">Beer Bank | {authState?.isAuthenticated ? 'The Personalized Cloud Dashboard' : 'The Cloud Dashboard'} {process.env.REACT_APP_VERSION}</h1>
              <p>Find your favourite beer here (or <i>open</i> your <u>personalized dashboard</u> to access your available services)</p>
            </div>
          </div>
          <Search />
          <div className="d-flex justify-content-center gap-2 mt-3">
            <TwitterShareButton url={window.location.href}><TwitterIcon size={32} round /></TwitterShareButton>
            <FacebookShareButton url={window.location.href}><FacebookIcon size={32} round /></FacebookShareButton>
            <TelegramShareButton url={window.location.href}><TelegramIcon size={32} round /></TelegramShareButton>
            <LinkedinShareButton url={window.location.href}><LinkedinIcon size={32} round /></LinkedinShareButton>
          </div>
          <div className="mt-2 text-center">
            <small>
              <span className="text-white-50">Missing expected health data or cards? </span>
              <Link to="/ecosystem/SER" className="text-white text-decoration-underline font-weight-bold" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.2)' }}>
                Check Synchronization Error Reporting (SER) &raquo;
              </Link>
            </small>
          </div>
        </div>
      </header>
      <Beers
        beers={beers}
        isLoading={isLoading}
        error={error}
        searchQuery={searchQuery}
        isGivingUp={isGivingUp}
        retryCount={attempts}
        isPcdUser={isAuthenticated}
        showNftCard={shouldShowNft}
        showAdCard={shouldShowAd}
      />
    </>
  );
};

// redux stuff
Home.propTypes = {
  fetchBeers: PropTypes.func.isRequired,
  fetchMoreBeers: PropTypes.func.isRequired,
  beers: PropTypes.array.isRequired,
  page: PropTypes.number.isRequired,
  isLoading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  searchQuery: PropTypes.string
};

const mapStateToProps = state => ({
  beers: state.beer.beers,
  page: state.beer.page,
  isLoading: state.beer.isLoading,
  error: state.beer.error,
  searchQuery: state.beer.searchQuery
});

export default connect(
  mapStateToProps,
  { fetchBeers, fetchMoreBeers }
)(Home);
